import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Common";
import { CaretLeft, CaretRight, Truck, WarningCircle, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const TZ = "Africa/Johannesburg";
const COLORS = ["#2563EB", "#16A34A", "#D97706", "#9333EA", "#DC2626", "#0891B2", "#DB2777", "#65A30D"];
const PX_PER_HOUR = 46;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd); }
function ymd(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// Local (SAST) date-key + minutes-from-midnight for an ISO timestamp.
function parts(iso) {
  const s = new Date(iso).toLocaleString("en-GB", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const [datePart, timePart] = s.split(", ");
  const [dd, mm, yy] = datePart.split("/");
  let [hh, mi] = timePart.split(":");
  if (hh === "24") hh = "00";
  return { key: `${yy}-${mm}-${dd}`, mins: (+hh) * 60 + (+mi), label: `${hh}:${mi}` };
}

// Greedy lane packing for overlapping time blocks within a column.
function packLanes(items) {
  const sorted = [...items].sort((a, b) => a.s - b.s || a.e - b.e);
  const laneEnds = [];
  sorted.forEach((it) => {
    let placed = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (it.s >= laneEnds[i]) { it.lane = i; laneEnds[i] = it.e; placed = true; break; }
    }
    if (!placed) { it.lane = laneEnds.length; laneEnds.push(it.e); }
  });
  const count = laneEnds.length || 1;
  sorted.forEach((it) => { it.laneCount = count; });
  return sorted;
}

export default function Calendar() {
  const [view, setView] = useState("month"); // month | week | day
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const navigate = useNavigate();

  // Range to fetch (padded ±1 day to be timezone-safe)
  const range = useMemo(() => {
    let start, end;
    if (view === "month") { start = startOfMonth(cursor); end = addMonths(start, 1); }
    else if (view === "week") { start = startOfWeek(cursor); end = addDays(start, 7); }
    else { start = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()); end = addDays(start, 1); }
    return { start: addDays(start, -1), end: addDays(end, 1) };
  }, [view, cursor]);

  useEffect(() => {
    api.get("/calendar/bookings", { params: { start: range.start.toISOString(), end: range.end.toISOString() } })
      .then((r) => setBookings(r.data));
  }, [range]);

  const vehicleColor = useMemo(() => {
    const map = {}; let i = 0;
    bookings.forEach((b) => { if (!(b.vehicle_id in map)) map[b.vehicle_id] = COLORS[i++ % COLORS.length]; });
    return map;
  }, [bookings]);

  const nav = (dir) => {
    if (view === "month") setCursor(addMonths(startOfMonth(cursor), dir));
    else if (view === "week") setCursor(addDays(startOfWeek(cursor), dir * 7));
    else setCursor(addDays(cursor, dir));
  };

  const label = useMemo(() => {
    if (view === "month") return cursor.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(cursor), e = addDays(s, 6);
      return `${s.toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [view, cursor]);

  const statusIcon = (status, size = 10) => {
    if (status === "draft") return <PencilSimple size={size} weight="bold" />;
    if (status === "completed") return <CheckCircle size={size} weight="fill" />;
    return <Truck size={size} weight="fill" />;
  };

  const blockStyle = (b) => {
    const color = vehicleColor[b.vehicle_id];
    if (b.status === "draft") return { className: "border border-dashed bg-white", style: { color, borderColor: color } };
    if (b.status === "completed") return { className: "text-white line-through opacity-60", style: { background: color } };
    return { className: "text-white", style: { background: color } };
  };

  const Legend = () => (
    <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="cal-status-legend">
      <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded bg-slate-700 text-white"><Truck size={9} weight="fill" /></span> Accepted</span>
      <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded border border-dashed border-slate-500 text-slate-600"><PencilSimple size={9} weight="bold" /></span> Draft</span>
      <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 rounded bg-slate-700 text-white opacity-60"><CheckCircle size={9} weight="fill" /></span> <span className="line-through">Completed</span></span>
    </div>
  );

  return (
    <div>
      <PageHeader title="Trip Calendar" subtitle="Accepted, draft and completed trips per vehicle. Overlaps for the same vehicle are flagged."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-md border border-slate-300 overflow-hidden" data-testid="cal-view-switch">
              {["month", "week", "day"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                  className={`px-3 py-1.5 text-sm capitalize ${view === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{v}</button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => nav(-1)} data-testid="cal-prev"><CaretLeft size={18} /></Button>
              <span className="font-heading font-semibold text-slate-900 min-w-[180px] text-center text-sm">{label}</span>
              <Button variant="outline" size="icon" onClick={() => nav(1)} data-testid="cal-next"><CaretRight size={18} /></Button>
            </div>
          </div>
        } />

      <Legend />

      {view === "month" && <MonthView cursor={cursor} bookings={bookings} vehicleColor={vehicleColor} blockStyle={blockStyle} statusIcon={statusIcon} navigate={navigate} />}
      {view === "week" && <WeekView cursor={cursor} bookings={bookings} blockStyle={blockStyle} statusIcon={statusIcon} navigate={navigate} />}
      {view === "day" && <DayView cursor={cursor} bookings={bookings} vehicleColor={vehicleColor} blockStyle={blockStyle} statusIcon={statusIcon} navigate={navigate} />}

      {/* Vehicle colour key */}
      <div className="mt-4 bg-white border border-slate-200 rounded-md p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Vehicles</div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(vehicleColor).map(([vid, color]) => {
            const b = bookings.find((x) => x.vehicle_id === vid);
            return <div key={vid} className="flex items-center gap-1.5 text-sm text-slate-700"><span className="w-3 h-3 rounded-sm" style={{ background: color }} />{b?.vehicle_name || vid}</div>;
          })}
          {bookings.length === 0 && <span className="text-sm text-slate-400">No trips in this range.</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Month view ---------------- */
function MonthView({ cursor, bookings, vehicleColor, blockStyle, statusIcon, navigate }) {
  const days = useMemo(() => {
    const first = startOfMonth(cursor);
    const startDay = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return cells;
  }, [cursor]);

  const onDay = (day) => {
    const ds = ymd(day);
    return bookings.filter((b) => { const s = parts(b.start).key, e = parts(b.end).key; return ds >= s && ds <= e; });
  };

  const conflictDays = useMemo(() => {
    const set = new Set();
    days.forEach((d) => {
      if (!d) return;
      const byVeh = {};
      onDay(d).forEach((b) => { byVeh[b.vehicle_id] = (byVeh[b.vehicle_id] || 0) + 1; });
      if (Object.values(byVeh).some((c) => c > 1)) set.add(ymd(d));
    });
    return set;
  }, [days, bookings]);

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => (
          <div key={i} className={`min-h-[96px] border-b border-r border-slate-100 p-1.5 ${!day ? "bg-slate-50/50" : ""} ${day && conflictDays.has(ymd(day)) ? "bg-red-50" : ""}`}>
            {day && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">{day.getDate()}</span>
                  {conflictDays.has(ymd(day)) && <WarningCircle size={14} className="text-red-500" weight="fill" />}
                </div>
                <div className="space-y-1 mt-1">
                  {onDay(day).slice(0, 3).map((b) => {
                    const cs = blockStyle(b);
                    return (
                      <button key={b.id} onClick={() => navigate(`/quotes/${b.quote_id}`)} data-testid={`booking-${b.id}`}
                        className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] font-medium truncate flex items-center gap-1 ${cs.className}`}
                        style={cs.style} title={`${b.vehicle_name} · ${b.title} · ${b.status}`}>
                        {statusIcon(b.status)}{b.vehicle_name || b.quote_number}
                      </button>
                    );
                  })}
                  {onDay(day).length > 3 && <div className="text-[10px] text-slate-400 px-1">+{onDay(day).length - 3} more</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Shared time-grid helpers ---------------- */
function hourRange(items) {
  let min = 6, max = 18;
  items.forEach((it) => { min = Math.min(min, Math.floor(it.s / 60)); max = Math.max(max, Math.ceil(it.e / 60)); });
  return { min: Math.max(0, min), max: Math.min(24, Math.max(max, min + 2)) };
}

function blocksForDay(bookings, dayKey) {
  const out = [];
  bookings.forEach((b) => {
    const ps = parts(b.start), pe = parts(b.end);
    if (dayKey < ps.key || dayKey > pe.key) return;
    const s = dayKey === ps.key ? ps.mins : 0;
    const e = dayKey === pe.key ? pe.mins : 24 * 60;
    out.push({ ...b, s, e: Math.max(e, s + 15), _start: ps.label, _end: pe.label });
  });
  return out;
}

/* ---------------- Day view ---------------- */
function DayView({ cursor, bookings, vehicleColor, blockStyle, statusIcon, navigate }) {
  const dayKey = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()));
  const dayBookings = blocksForDay(bookings, dayKey);
  const vehicles = useMemo(() => {
    const seen = {};
    dayBookings.forEach((b) => { seen[b.vehicle_id] = b.vehicle_name || b.quote_number; });
    return Object.entries(seen);
  }, [bookings, dayKey]);
  const { min, max } = hourRange(dayBookings);
  const hours = []; for (let h = min; h <= max; h++) hours.push(h);
  const gridH = (max - min) * PX_PER_HOUR;

  if (vehicles.length === 0) return <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400" data-testid="cal-day-empty">No trips scheduled on this day.</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto cd-scroll" data-testid="cal-day-view">
      <div className="flex min-w-max">
        {/* Time gutter */}
        <div className="w-14 shrink-0 border-r border-slate-100">
          <div className="h-9 border-b border-slate-200 bg-slate-50" />
          <div className="relative" style={{ height: gridH }}>
            {hours.map((h, idx) => (
              <div key={h} className="absolute right-1 text-[10px] text-slate-400 -translate-y-1/2" style={{ top: idx * PX_PER_HOUR }}>{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
        </div>
        {/* Vehicle columns */}
        {vehicles.map(([vid, vname]) => {
          const items = packLanes(dayBookings.filter((b) => b.vehicle_id === vid));
          return (
            <div key={vid} className="w-56 shrink-0 border-r border-slate-100">
              <div className="h-9 border-b border-slate-200 bg-slate-50 px-2 flex items-center gap-1.5 text-xs font-semibold text-slate-700 truncate">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: vehicleColor[vid] }} />{vname}
              </div>
              <div className="relative" style={{ height: gridH }}>
                {hours.map((h, idx) => <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: idx * PX_PER_HOUR }} />)}
                {items.map((b) => {
                  const cs = blockStyle(b);
                  const top = ((b.s - min * 60) / 60) * PX_PER_HOUR;
                  const height = Math.max(((b.e - b.s) / 60) * PX_PER_HOUR - 2, 22);
                  const w = 100 / b.laneCount;
                  return (
                    <button key={b.id} onClick={() => navigate(`/quotes/${b.quote_id}`)} data-testid={`booking-${b.id}`}
                      className={`absolute rounded px-1.5 py-1 text-[10px] font-medium overflow-hidden text-left ${cs.className}`}
                      style={{ ...cs.style, top, height, left: `calc(${b.lane * w}% + 2px)`, width: `calc(${w}% - 4px)` }}
                      title={`${b.client_name || ""} · ${b._start}–${b._end} · ${b.status}`}>
                      <span className="flex items-center gap-1">{statusIcon(b.status, 9)}{b._start}–{b._end}</span>
                      <span className="block truncate opacity-90">{b.client_name || b.quote_number}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Week view ---------------- */
function WeekView({ cursor, bookings, blockStyle, statusIcon, navigate }) {
  const weekStart = startOfWeek(cursor);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [cursor]);
  const dayItems = days.map((d) => blocksForDay(bookings, ymd(d)));
  const allItems = dayItems.flat();
  const { min, max } = hourRange(allItems);
  const hours = []; for (let h = min; h <= max; h++) hours.push(h);
  const gridH = (max - min) * PX_PER_HOUR;
  const todayKey = ymd(new Date());

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto cd-scroll" data-testid="cal-week-view">
      <div className="flex min-w-[900px]">
        {/* Time gutter */}
        <div className="w-14 shrink-0 border-r border-slate-100">
          <div className="h-10 border-b border-slate-200 bg-slate-50" />
          <div className="relative" style={{ height: gridH }}>
            {hours.map((h, idx) => (
              <div key={h} className="absolute right-1 text-[10px] text-slate-400 -translate-y-1/2" style={{ top: idx * PX_PER_HOUR }}>{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
        </div>
        {/* Day columns */}
        {days.map((d, di) => {
          const items = packLanes(dayItems[di]);
          const isToday = ymd(d) === todayKey;
          return (
            <div key={di} className="flex-1 min-w-[110px] border-r border-slate-100">
              <div className={`h-10 border-b border-slate-200 flex flex-col items-center justify-center ${isToday ? "bg-teal-50" : "bg-slate-50"}`}>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{WEEKDAYS[di]}</span>
                <span className={`text-xs font-semibold ${isToday ? "text-teal-700" : "text-slate-700"}`}>{d.getDate()}</span>
              </div>
              <div className="relative" style={{ height: gridH }}>
                {hours.map((h, idx) => <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: idx * PX_PER_HOUR }} />)}
                {items.map((b) => {
                  const cs = blockStyle(b);
                  const top = ((b.s - min * 60) / 60) * PX_PER_HOUR;
                  const height = Math.max(((b.e - b.s) / 60) * PX_PER_HOUR - 2, 20);
                  const w = 100 / b.laneCount;
                  return (
                    <button key={b.id} onClick={() => navigate(`/quotes/${b.quote_id}`)} data-testid={`booking-${b.id}`}
                      className={`absolute rounded px-1 py-0.5 text-[9px] font-medium overflow-hidden text-left leading-tight ${cs.className}`}
                      style={{ ...cs.style, top, height, left: `calc(${b.lane * w}% + 1px)`, width: `calc(${w}% - 2px)` }}
                      title={`${b.vehicle_name} · ${b.client_name || ""} · ${b._start}–${b._end} · ${b.status}`}>
                      <span className="flex items-center gap-0.5">{statusIcon(b.status, 8)}{b._start}</span>
                      <span className="block truncate opacity-90">{b.vehicle_name || b.quote_number}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
